import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Column, Lookup } from 'devextreme-react/data-grid';
import Guid from "../../models/Guid";
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { SimpleItem, GroupItem, Label, FormRef } from "devextreme-react/form";
import ITypePaiement from "../../models/ITypePaiement";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import { useContextLeftSidebarData } from '../../contexts/refreshDataSource';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionFolios from '../gestion-folios/gestion-folios';
import { Button, FileUploader, Gallery, LoadIndicator } from 'devextreme-react';
import useEnum from '../../hooks/useEnum';

interface IGestionTypesPaiementsProps {
  isModeSelection?: boolean,
}

export default function GestionTypesPaiements({ isModeSelection }: IGestionTypesPaiementsProps) {
  const { displayedElementInactif, setDisplayedElementInactif } = useContextLeftSidebarData();
  const clientFormDataSource = useCustomDataSource<ITypePaiement, ITypePaiement>({ path: "typePaiement", primaryKey: "idTypePaiement", includeInactives: displayedElementInactif });
  const { setMainForm, selectedDataRow: selectedTypePaiement, mode, setMode, setSelectedDataRow, isFieldModifier, setIsFieldModifier } = clientFormDataSource;
  const { editorOptions: folioEditorOption, PopupGestion: PopupGestionFolios } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "folio",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idFolio", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture, width: '100%'
  });
  const formRef = useRef<FormRef>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const { editorOptions: modePaiementEditorOptions } = useEnum(undefined, "gestionModePaiement", '69%');

  // Cet useEffect permet de renitialiser la valueur boolean du hook useContextLeftSidebarData pour rafraichir les donnees
  useEffect(() => {
    setDisplayedElementInactif(false)
  }, [])

  const descriptionOptions = useMemo(() => {
    return { maxLength: 40, width: '100%' }
  }, []);
  const motDePasseEditorOptions = useMemo(() => {
    return { width: '56%' }
  }, []);
  const tauxChangeEditorOptions = useMemo(() => {
    return {
      format: {
        type: "fixedPoint",
        precision: 3,
      },
      width: '36%',
    }
  }, []);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])

  const onSelectionchanged = (item: any, e: string) => {
    setIsFieldModifier(true);
    setSelectedDataRow((prev: ITypePaiement) => {
      return { ...prev, [e]: item.id }
    })
  }

  const cellRenderTauxChange = (e: any) => {
    if (!e.data.tauxChange)
      return '0,000'
    return e.data.tauxChange.toFixed
      ? e.data.tauxChange.toFixed(3).replace('.', ',')
      : e.data.tauxChange;
  }

  useEffect(() => {
    if (selectedTypePaiement) {
      if (selectedTypePaiement?.image) {
        const img = new Image();
        const dataUrl = `data:image/jpeg;base64,${selectedTypePaiement.image}`;
        img.onload = () => {
          setImageSrc(dataUrl);
          setImageLoading(false);
        };
        img.onerror = () => {
          setImageSrc(null);
          setImageLoading(false);
        };
        img.src = dataUrl;
      } else {
        setImageSrc('');
        setImageLoading(false);
      }
    } else {
      setImageSrc(null);
      setImageLoading(true);
    }
  }, [selectedTypePaiement]);

  return (
    <FormListBase data={clientFormDataSource}>
      <FormListBaseGrid dataSource={clientFormDataSource.dataSource}>
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-id-folio"} dataField={"idFolio"} caption="Folio" allowHiding={false}>
          <Lookup {...folioEditorOption} />
        </Column>
        <Column key={"grid-col-mode-paiement"} dataField={"modePaiement"} caption="Mode de paiement" allowHiding={false}>
          <Lookup {...modePaiementEditorOptions} />
        </Column>
        <Column key={"grid-col-taux-change"}
          // dataField={"tauxChange"} 
          caption="Taux de change" allowHiding={false}
          cellRender={(e) => cellRenderTauxChange(e)}
          alignment='right'
        />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedTypePaiement?.description || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture ? <FormListBaseTab>
        <TabItem title="Informations">
          <PopupGestionFolios
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idFolio", data.idFolio);
            }}
          >
            <GestionFolios isModeSelection={true} />
          </PopupGestionFolios>
          <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedTypePaiement || {} as ITypePaiement} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={(e) => setIsFieldModifier(true)}>
            <GroupItem colCount={5}>
              <GroupItem colSpan={2}>
                <SimpleItem key={"frm-description"} dataField={"description"}
                  editorOptions={descriptionOptions} />
                <SimpleItem key={"frm-folio"} dataField={"idFolio"} editorType={"dxSelectBox"} editorOptions={folioEditorOption}>
                  <Label text={"Folio"} />
                </SimpleItem>
                <SimpleItem key={"frm-mode-paiement"} dataField={"modePaiement"} editorType={"dxSelectBox"} editorOptions={modePaiementEditorOptions}>
                  <Label text={"Mode de paiement"} />
                </SimpleItem>
                {selectedTypePaiement?.modePaiement != 8 &&
                  <SimpleItem key={"frm-tauxChange"} dataField={"tauxChange"} editorOptions={tauxChangeEditorOptions}>
                    <Label text={"Taux de change"} />
                  </SimpleItem>
                }
                <SimpleItem key={"frm-mot-de-passe"} dataField={"motDePasse"} editorOptions={motDePasseEditorOptions}>
                  <Label text={"Mot de passe"} />
                </SimpleItem>
              </GroupItem>
              <GroupItem colSpan={3}>
                <GroupItem caption='Options'>
                  <SimpleItem key={"frm-actif"} dataField={"actif"} editorType='dxCheckBox'
                    editorOptions={{ text: "Actif" }}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-integreTpv"} dataField={"integreTpv"} editorType='dxCheckBox'
                    editorOptions={{ text: "Intégration TPV" }}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-pasAfficherPos"} dataField={"pasAfficherPos"} editorType='dxCheckBox'
                    editorOptions={{ text: "Ne pas afficher dans le POS" }}>
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
                <GroupItem caption='Image'>
                  <GroupItem colCount={4}>
                    <SimpleItem>
                      <FileUploader
                        disabled={mode === Mode.Lecture}
                        onValueChange={(e) => {
                          const file = e?.[0];
                          if (file) {
                            setImageLoading(true);
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              const result = event.target?.result as string;
                              const base64 = result.split(',')[1];
                              setImageSrc(result);
                              setImageLoading(false);
                              setSelectedDataRow((prevState: ITypePaiement) => ({
                                ...prevState,
                                image: base64
                              }));
                              setIsFieldModifier(true);
                            };
                            reader.onerror = () => {
                              setImageLoading(false);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        showFileList={false}
                        selectButtonText="Ajouter"
                        labelText=""
                        accept="image/*"
                        uploadMode="useForm"
                      />
                    </SimpleItem>
                    <SimpleItem>
                      <Button
                        disabled={mode == Mode.Lecture}
                        style={{
                          position: 'relative',
                          top: '14px',
                        }}
                        onClick={() => {
                          setImageSrc(null);
                          setSelectedDataRow((prev: ITypePaiement) => ({
                            ...prev,
                            image: ''
                          }));
                          setIsFieldModifier(true);
                        }}
                        text="Supprimer"
                      />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem>
                    <div style={{ position: 'relative', width: 260, height: 260 }}>
                      {imageLoading || !selectedTypePaiement && (
                        <LoadIndicator
                          visible={true}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1
                          }}
                        />
                      )}
                      <Gallery
                        dataSource={imageSrc ? [imageSrc] : []}
                        noDataText=''
                        width={260}
                        height={260}
                        showIndicator={false}
                      />
                    </div>
                  </GroupItem>
                </GroupItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedTypePaiement || {} as ITypePaiement} /></TabItem>
      </FormListBaseTab> : null}
    </FormListBase>
  );
}